import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import TaskService from '@frontend/AppSettings/services/TaskService';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';
import { format, parseISO, isToday, isTomorrow, differenceInDays, isValid } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { isEngineeringEnvironment } from '@frontend/AppSettings/environmentConfig';
import './ActionItemsWidget.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export interface ActionItemsWidgetProps {
  className?: string;
}

type UrgencyLevel = 'Urgent' | 'High' | 'Medium' | 'Low';

interface ActionItem {
  id: string;
  title: string;
  matterName?: string;
  projectName?: string;
  dueDate: Date;
  urgency: UrgencyLevel;
  task: any; // Store the full task object for navigation
}

/**
 * ActionItemsWidget Component
 * 
 * Displays a bar chart showing action items by urgency level and
 * a list of specific action items sorted by due date with color-coded alerts.
 * 
 * Features:
 * - Fetches tasks from TaskService
 * - Categorizes tasks by urgency (Urgent, High, Medium, Low)
 * - Displays bar chart for urgency distribution
 * - Lists upcoming action items with due dates
 * - Color-coded alerts based on urgency
 */
const ActionItemsWidget: React.FC<ActionItemsWidgetProps> = ({
  className = '',
}) => {
  const employeeId = useActiveEmployeeId();
  const navigate = useNavigate();

  // Fetch tasks for the current user
  const { data: tasks, isPending, error } = useQuery({
    queryKey: ['Tasks', 'dashboard', employeeId],
    queryFn: () => 
      employeeId 
        ? TaskService.getTasksByEmployeeId(employeeId)
        : TaskService.getAllTasks(),
    enabled: !!employeeId || true,
  });

  // Process tasks into action items with urgency levels
  // Returns exactly 4 items: one for each urgency level (Urgent, High, Medium, Low)
  // Each item is the one with the earliest due date for that urgency level
  const actionItems = useMemo(() => {
    if (!tasks || !Array.isArray(tasks)) return [];

    const now = new Date();
    
    // Filter out completed tasks and map to ActionItems
    const allActionItems = tasks
      .filter((task: any) => {
        // Only show pending or in-progress tasks (exclude completed)
        const status = (task.status || '').toLowerCase();
        return status === 'pending' || status === 'in progress';
      })
      .map((task: any): ActionItem => {
        let dueDate: Date;
        if (task.duedate) {
          const parsedDate = typeof task.duedate === 'string' ? parseISO(task.duedate) : new Date(task.duedate);
          dueDate = isValid(parsedDate) ? parsedDate : new Date();
        } else {
          dueDate = new Date();
        }
        
        const daysUntilDue = differenceInDays(dueDate, now);
        
        // Determine urgency based on days until due
        let urgency: UrgencyLevel = 'Low';
        if (daysUntilDue < 0) {
          urgency = 'Urgent'; // Overdue
        } else if (daysUntilDue === 0) {
          urgency = 'Urgent'; // Due today
        } else if (daysUntilDue <= 1) {
          urgency = 'High'; // Due tomorrow or next day
        } else if (daysUntilDue <= 7) {
          urgency = 'Medium'; // Due within a week
        } else {
          urgency = 'Low'; // Due later
        }

        return {
          id: task.taskid || task.id,
          title: task.task_title || task.title || 'Untitled Task',
          matterName: task.mattername || task.matter_name,
          projectName: task.projectname || task.project_name,
          dueDate,
          urgency,
          task, // Store the full task object
        };
      })
      .sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime()); // Sort all by due date

    // Group by urgency level and get the earliest due date item for each level
    const urgencyGroups: Record<UrgencyLevel, ActionItem[]> = {
      Urgent: [],
      High: [],
      Medium: [],
      Low: [],
    };

    // Group items by urgency
    allActionItems.forEach((item) => {
      urgencyGroups[item.urgency].push(item);
    });

    // Get the earliest due date item for each urgency level
    const result: ActionItem[] = [];
    const urgencyOrder: UrgencyLevel[] = ['Urgent', 'High', 'Medium', 'Low'];
    
    urgencyOrder.forEach((urgency) => {
      if (urgencyGroups[urgency].length > 0) {
        // Already sorted by due date, so first item is earliest
        result.push(urgencyGroups[urgency][0]);
      }
    });

    return result; // Returns up to 4 items, one per urgency level
  }, [tasks]);

  // Calculate urgency distribution for bar chart
  const urgencyData = useMemo(() => {
    const counts = {
      Urgent: 0,
      High: 0,
      Medium: 0,
      Low: 0,
    };

    if (tasks && Array.isArray(tasks)) {
      const now = new Date();
      tasks
        .filter((task: any) => {
          const status = (task.status || '').toLowerCase();
          return status === 'pending' || status === 'in progress';
        })
        .forEach((task: any) => {
          let dueDate: Date;
          if (task.duedate) {
            const parsedDate = typeof task.duedate === 'string' ? parseISO(task.duedate) : new Date(task.duedate);
            dueDate = isValid(parsedDate) ? parsedDate : new Date();
          } else {
            dueDate = new Date();
          }
          const daysUntilDue = differenceInDays(dueDate, now);
          
          if (daysUntilDue < 0 || daysUntilDue === 0) {
            counts.Urgent++;
          } else if (daysUntilDue <= 1) {
            counts.High++;
          } else if (daysUntilDue <= 7) {
            counts.Medium++;
          } else {
            counts.Low++;
          }
        });
    }

    return counts;
  }, [tasks]);

  // Prepare bar chart data
  const chartData = {
    labels: ['Urgent', 'High', 'Medium', 'Low'],
    datasets: [
      {
        label: 'Action Items',
        data: [
          urgencyData.Urgent,
          urgencyData.High,
          urgencyData.Medium,
          urgencyData.Low,
        ],
        backgroundColor: ['#dc3545', '#fd7e14', '#007bff', '#28a745'],
        borderColor: ['#dc3545', '#fd7e14', '#007bff', '#28a745'],
        borderWidth: 1,
      },
    ],
  };

  // Handle chart bar click
  const handleChartClick = (event: any, elements: any[]) => {
    if (elements && elements.length > 0) {
      const clickedElement = elements[0];
      const categoryIndex = clickedElement.index;
      const categories = ['Urgent', 'High', 'Medium', 'Low'];
      const selectedCategory = categories[categoryIndex];
      
      // Navigate to tasks page with urgency filter and my-tasks tab
      navigate(`/tasks?tab=my-tasks&urgency=${selectedCategory}`);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.parsed.y} items`,
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          precision: 0,
        },
        grid: {
          color: '#f0f0f0',
        },
      },
    },
    onClick: handleChartClick,
    onHover: (event: any, elements: any[]) => {
      // Change cursor to pointer when hovering over bars
      if (elements && elements.length > 0) {
        event.native.target.style.cursor = 'pointer';
      } else {
        event.native.target.style.cursor = 'default';
      }
    },
  };

  // Format due date for display (matching the design exactly)
  const formatDueDate = (date: Date): string => {
    // Check if the date is valid before formatting
    if (!isValid(date)) {
      return 'Invalid Date';
    }
    
    if (isToday(date)) {
      return `Today, ${format(date, 'h:mm a')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow, ${format(date, 'h:mm a')}`;
    } else {
      return format(date, 'MMM d, yyyy');
    }
  };

  // Get urgency icon and color
  const getUrgencyStyle = (urgency: UrgencyLevel) => {
    switch (urgency) {
      case 'Urgent':
        return { icon: faExclamationCircle, color: '#dc3545' };
      case 'High':
        return { icon: faExclamationCircle, color: '#fd7e14' };
      case 'Medium':
        return { icon: faInfoCircle, color: '#007bff' };
      case 'Low':
        return { icon: faInfoCircle, color: '#28a745' };
      default:
        return { icon: faInfoCircle, color: '#6c757d' };
    }
  };

  if (isPending) {
    return (
      <div className={`action-items-widget ${className}`}>
        <div className="action-items-header">
          <h3 className="action-items-title">My Action Items</h3>
        </div>
        <div className="action-items-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`action-items-widget ${className}`}>
        <div className="action-items-header">
          <h3 className="action-items-title">My Action Items</h3>
        </div>
        <div className="action-items-error">Error loading data</div>
      </div>
    );
  }

  return (
    <div className={`action-items-widget ${className}`}>
      <div className="action-items-header">
        <h3 className="action-items-title">My Action Items</h3>
        <a href="/tasks" className="action-items-view-all">View All</a>
      </div>

      <div className="action-items-content">
        {/* Bar Chart */}
        <div className="action-items-chart-container">
          <Bar data={chartData} options={chartOptions} />
        </div>

        {/* Action Items List - Shows exactly 4 notifications (one per urgency level) */}
        <div className="action-items-list">
          {actionItems.length > 0 ? (
            actionItems.map((item) => {
              const urgencyStyle = getUrgencyStyle(item.urgency);
              // Combine title and matter/project name into one line
              const entityName = isEngineeringEnvironment 
                ? (item.projectName || item.matterName)
                : (item.matterName || item.projectName);
              const displayTitle = entityName 
                ? `${item.title} - ${entityName}`
                : item.title;
              
              const handleActionItemClick = () => {
                // Navigate to tasks page with taskId to open the details panel
                navigate(`/tasks?taskId=${item.id}`);
              };
              
              return (
                <div 
                  key={item.id} 
                  className="action-item action-item-clickable"
                  style={{ borderLeftColor: urgencyStyle.color }}
                  onClick={handleActionItemClick}
                >
                  <div className="action-item-icon-wrapper" style={{ color: urgencyStyle.color }}>
                    <FontAwesomeIcon 
                      icon={urgencyStyle.icon} 
                      className="action-item-icon"
                    />
                  </div>
                  <div className="action-item-content">
                    <div className="action-item-title">{displayTitle}</div>
                    <div className="action-item-due">
                      Due: {formatDueDate(item.dueDate)}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="action-items-empty">No action items</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActionItemsWidget;

